import CircularProgress from "@mui/material/CircularProgress";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchDeclineContext, submitDecline } from "@/api/decline";
import { GFG_LOGO } from "@/constants/campaignBuilder";
import { DECLINE_REASONS, type DeclineContext, type DeclineReason } from "@/interfaces/decline";

/**
 * Public "decline my gift" page — the v1 flow (`views/customer/decline-gift.ejs`) rebuilt on this
 * codebase's shapes. Reached from the link in a reminder e-mail.
 *
 * Mounted OUTSIDE RequireAuth, like `/g/:token`: recipients are not Clerk users, and the signed
 * `?token=` IS the credential. The query-string shape is v1's and is kept deliberately — links
 * already sitting in recipients' inboxes point at exactly this URL.
 *
 * Declining is once and final. The server enforces that; this page never re-offers the form after
 * a decline, which is the half v1 got wrong (`declined` was missing from its `inactiveStatuses`,
 * so the form came back and overwrote the first reason).
 */

type Phase = "loading" | "form" | "done" | "closed" | "invalid";

export default function DeclineGiftPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const isPreview = params.get("preview") === "1";

  const [phase, setPhase] = useState<Phase>("loading");
  const [context, setContext] = useState<DeclineContext | null>(null);
  const [reason, setReason] = useState<DeclineReason | null>(null);
  const [reasonText, setReasonText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setPhase("invalid");
      return;
    }
    if (isPreview) {
      // Nothing to resolve: show the form the recipient would get, against no contact.
      setPhase("form");
      return;
    }
    let cancelled = false;
    fetchDeclineContext(token)
      .then((ctx) => {
        if (cancelled) return;
        setContext(ctx);
        // `alreadyDeclined` before `open`: both are false for a declined contact, and the recipient
        // should be told their decline landed rather than that the link is spent.
        setPhase(ctx.alreadyDeclined ? "done" : ctx.open ? "form" : "closed");
      })
      .catch(() => {
        if (!cancelled) setPhase("invalid");
      });
    return () => {
      cancelled = true;
    };
  }, [token, isPreview]);

  async function handleSubmit() {
    if (!reason || submitting) return;
    if (isPreview) {
      // A preview stops here: the sender sees the confirmation, no contact is touched.
      setPhase("done");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await submitDecline(token, {
        reason,
        ...(reason === "other" ? { reasonText: reasonText.trim() } : {}),
      });
      setPhase("done");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Please try again.");
      setSubmitting(false);
    }
  }

  const whose = context?.senderName ? `your gift from ${context.senderName}` : "your gift";
  const canSubmit = reason !== null && (reason !== "other" || reasonText.trim().length > 0);

  return (
    <main className="gfg-auth-screen">
      <div className="gfg-auth-card">
        <img src={GFG_LOGO} alt="Gifts for Good" style={{ height: 34, margin: "0 auto 24px" }} />

        {phase === "loading" && (
          <div style={{ display: "grid", placeItems: "center", padding: "24px 0" }}>
            <CircularProgress aria-label="Loading" sx={{ color: "var(--color-dark-dust)" }} />
          </div>
        )}

        {phase === "invalid" && (
          <>
            <h1 className="gfg-auth-title">Link not found</h1>
            <p className="gfg-auth-sub">
              This link is no longer valid. If you still have the original email, try opening it
              from there.
            </p>
          </>
        )}

        {phase === "closed" && (
          <>
            <h1 className="gfg-auth-title">Nothing to decline</h1>
            <p className="gfg-auth-sub">
              This gift has already been claimed or is no longer available, so there is nothing left
              to decline.
            </p>
          </>
        )}

        {phase === "done" && (
          <>
            <h1 className="gfg-auth-title">Thank you</h1>
            <p className="gfg-auth-sub">
              Your gift has been declined. You won't receive any more reminders about it.
            </p>
          </>
        )}

        {phase === "form" && (
          <>
            <h1 className="gfg-auth-title">Decline {whose}</h1>
            <p className="gfg-auth-sub">Please select a reason for declining:</p>

            <div style={{ display: "flex", flexDirection: "column", gap: 10, margin: "20px 0" }}>
              {DECLINE_REASONS.map((option) => (
                <label
                  key={option}
                  style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}
                >
                  <input
                    type="radio"
                    name="reason"
                    checked={reason === option}
                    onChange={() => setReason(option)}
                    style={{ marginTop: 3 }}
                  />
                  <span style={{ fontSize: 14, color: "var(--color-rodeo-black)" }}>{option}</span>
                </label>
              ))}

              <label style={{ display: "flex", gap: 10, alignItems: "center", cursor: "pointer" }}>
                <input
                  type="radio"
                  name="reason"
                  checked={reason === "other"}
                  onChange={() => setReason("other")}
                />
                <span style={{ fontSize: 14, color: "var(--color-rodeo-black)" }}>Other</span>
              </label>

              {reason === "other" && (
                <input
                  type="text"
                  value={reasonText}
                  onChange={(e) => setReasonText(e.target.value)}
                  placeholder="Please give a reason"
                  maxLength={200}
                  aria-label="Your reason for declining"
                  style={{
                    marginLeft: 26,
                    padding: "10px 12px",
                    border: "1px solid var(--color-smoke)",
                    borderRadius: "var(--radius-btn, 6px)",
                    fontFamily: "var(--font-lato)",
                    fontSize: 14,
                  }}
                />
              )}
            </div>

            {error && (
              <p
                className="gfg-auth-hint"
                style={{ color: "var(--color-good-wine)", marginBottom: 8 }}
              >
                {error}
              </p>
            )}

            <button
              type="button"
              className="gfg-auth-btn"
              disabled={!canSubmit || submitting}
              onClick={handleSubmit}
            >
              {submitting ? (
                <CircularProgress aria-label="Submitting" size={18} sx={{ color: "white" }} />
              ) : (
                "Decline your gift"
              )}
            </button>
          </>
        )}
      </div>
    </main>
  );
}
